import type { GitHubConnectionStatus } from "@app/lib/skill_detection";
import { useDisconnectWorkspaceGitHub } from "@app/lib/swr/github_connection";
import type { LightWorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import {
  Avatar,
  Button,
  Chip,
  DotsHorizontal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  GithubLogo,
  Tooltip,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";

interface GitHubConnectionRowProps {
  owner: LightWorkspaceType;
  connection: GitHubConnectionStatus;
  onDisconnected: () => void;
}

export function GitHubConnectionRow({
  owner,
  connection,
  onDisconnected,
}: GitHubConnectionRowProps) {
  const { t } = useLingui();
  const { disconnectGitHub, isDisconnectingGitHub } =
    useDisconnectWorkspaceGitHub({ owner });

  const handleDisconnect = async () => {
    const disconnected = await disconnectGitHub();
    if (disconnected) {
      onDisconnected();
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3">
      <Avatar icon={GithubLogo} size="sm" />
      <div className="flex grow flex-col">
        <span className="heading-sm text-foreground">GitHub</span>
        <span className="text-sm text-muted-foreground">
          <Trans>Access repository - Shared connection</Trans>
        </span>
      </div>
      <Chip size="xs" color="success" label={t`Connected`} />
      {connection.connectedBy && (
        <Tooltip
          label={connection.connectedBy.fullName}
          tooltipTriggerAsChild
          trigger={
            <div className="cursor-default">
              <Avatar
                size="xxs"
                name={connection.connectedBy.fullName}
                visual={connection.connectedBy.imageUrl ?? undefined}
              />
            </div>
          }
        />
      )}
      {isAdmin(owner) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isDisconnectingGitHub}>
            <Button variant="ghost" size="sm" icon={DotsHorizontal} />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              label={t`Disconnect`}
              onClick={() => {
                void handleDisconnect();
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
